"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1068 — KUK dashboard-access band.
 *
 * Port of kuk-campus-portal `components/Referral/DashboardAccess.tsx`: a short band
 * of artwork carrying a large icon, one bold line and a bordered CTA.
 *
 * TWO ICONS, TWO ROLES. The reference draws a `lucide-react` `Gift` above the
 * heading and a second small icon inside the button. Both are CMS fields here
 * (`icon` and `download_brochure_text`'s partner `alt_icon`), and both accept either
 * an icon-map key or an uploaded image URL, so the reference's artwork carries over
 * without importing an icon library for two glyphs.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * BACKGROUND ARTWORK — v1 uses `next/image` with `fill` for both the mobile and
 * desktop artwork, keeping them as real images (so they get optimised and sized)
 * rather than CSS backgrounds. That is kept.
 *
 * NOT CARRIED OVER — v1's hardcoded `/launchpad` href, which is `apply_now_link`
 * here, and its `shadow-dashboard-access-shadow` utility, a project-level class this
 * app has no equivalent for; the CTA keeps its border and drops the glow.
 */

interface Banner1068Data {
  /** Artwork from 768px up. */
  background_image?: string;
  /** Artwork below 768px; falls back to the desktop artwork. */
  background_image_mobile?: string;
  image_alt?: string;
  /** Large icon above the heading — icon-map key or uploaded image URL. */
  icon?: string;
  icon_color?: string;
  title?: string;
  title_color?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  apply_now_border_color?: string;
  /** Small icon inside the CTA — icon-map key or uploaded image URL. */
  alt_icon?: string;
  status?: boolean;
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const Banner1068 = (props: { data?: Banner1068Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1068Data | undefined;
  const dispatch = useDispatch();

  const {
    background_image = "",
    background_image_mobile = "",
    image_alt = "",
    icon = "",
    icon_color = "#FFFFFF",
    title = "",
    title_color = "#FFFFFF",
    apply_now_text = "Refer & Earn",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    apply_now_border_color = "#FFFFFF",
    alt_icon = "",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1068",
      component: "dashboard_access_cta",
      label: apply_now_text?.trim() || "refer_and_earn",
      step: "1",
      entity: "banner_1068",
      parentEntity: "banner_1068",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status) return null;

  const desktopArt = background_image || background_image_mobile;
  const mobileArt = background_image_mobile || background_image;

  const ctaClassName = cn(
    "flex items-center justify-center gap-2 self-start rounded border",
    "px-6 py-2.5 text-xs font-medium tab:text-base",
  );
  const ctaStyle = {
    color: apply_now_color,
    borderColor: apply_now_border_color,
  };
  const ctaInner = (
    <>
      {alt_icon ? (
        <DynamicIcon
          iconName={alt_icon}
          width="20"
          height="20"
          color={apply_now_color}
        />
      ) : null}
      <span>{apply_now_text}</span>
    </>
  );

  return (
    <section className="relative w-full px-6 py-12 tab:pl-[75px]">
      <div className="absolute inset-0 z-0 size-full">
        {mobileArt ? (
          <Image
            src={mobileArt}
            alt={image_alt || ""}
            fill
            loading="lazy"
            sizes="100vw"
            className="block object-cover tab:hidden"
          />
        ) : null}
        {desktopArt ? (
          <Image
            src={desktopArt}
            alt={image_alt || ""}
            fill
            loading="lazy"
            sizes="100vw"
            className="hidden object-cover tab:block"
          />
        ) : null}
      </div>

      <div className="relative z-10 flex flex-col gap-6 tab:gap-2.5">
        {icon ? (
          <span className="block">
            <DynamicIcon iconName={icon} width="54" height="54" color={icon_color} />
          </span>
        ) : null}
        {title ? (
          <div className="max-w-[126px] tab:max-w-full">
            <p
              className="text-xl font-bold leading-normal tab:text-[32px]"
              style={{ color: title_color }}
            >
              {title}
            </p>
          </div>
        ) : null}
        {applyNowShow && apply_now_text ? (
          applyNowIsCta || !apply_now_link ? (
            <button
              type="button"
              onClick={handleApplyNowClick}
              className={ctaClassName}
              style={ctaStyle}
            >
              {ctaInner}
            </button>
          ) : (
            <Link
              href={normalizeHref(apply_now_link)}
              className={ctaClassName}
              style={ctaStyle}
            >
              {ctaInner}
            </Link>
          )
        ) : null}
      </div>
    </section>
  );
};

export default Banner1068;
